import React, { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart } from 'recharts';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../auth/AuthContext';
import { LoadingSpinner } from '../common/Loading';
export function Dashboard() {
  const {
    user
  } = useAuth();
  const [stats, setStats] = useState({
    totalPremium: 0,
    totalClaims: 0,
    lossRatio: 0
  });
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;
      try {
        // Fetch all records for this user
        // Note: For large datasets, we should do aggregation on the backend/server-side
        // But for this demo with < 1000 records, client-side aggregation is fine
        const q = query(collection(db, 'bordereau_records'), where('uploadedBy', '==', user.uid));
        const querySnapshot = await getDocs(q);
        let totalGwp = 0;
        const monthlyData: Record<string, {
          premium: number;
          claims: number;
        }> = {};
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const gwp = Number(data.gwp) || 0;
          totalGwp += gwp;

          // Parse date - assuming format like "2023-01-15" or similar standard formats
          // If date is simple string like "Jan 2023", we might need better parsing
          // For now, let's try to parse standard dates
          let dateObj = new Date(data.date);
          if (isNaN(dateObj.getTime())) {
            // Fallback for simple formats if needed, or just skip
            return;
          }
          const monthYear = dateObj.toLocaleString('default', {
            month: 'short',
            year: 'numeric'
          }); // e.g., "Jan 2023"

          if (!monthlyData[monthYear]) {
            monthlyData[monthYear] = {
              premium: 0,
              claims: 0
            };
          }
          monthlyData[monthYear].premium += gwp;
        });
        setStats({
          totalPremium: totalGwp,
          totalClaims: 0,
          // No claims data yet
          lossRatio: 0
        });

        // Convert monthlyData to array and sort
        const processedChartData = Object.entries(monthlyData).map(([date, values]) => {
          const [month, year] = date.split(' ');
          return {
            date,
            month,
            year,
            premium: Math.round(values.premium),
            claims: 0,
            lossRatio: 0
          };
        }).sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });
        setChartData(processedChartData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // Heatmap data generation from real data
  const createHeatmapData = () => {
    if (chartData.length === 0) return [];
    const years = Array.from(new Set(chartData.map(d => d.year))).sort();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map(month => {
      const monthData: any = {
        month
      };
      years.forEach(year => {
        const dataPoint = chartData.find(d => d.month === month && d.year === year);
        monthData[year] = dataPoint ? dataPoint.lossRatio : 0;
      });
      return monthData;
    });
  };
  const heatmapData = createHeatmapData();
  const years = Array.from(new Set(chartData.map(d => d.year))).sort();
  const pieData = [{
    name: 'Carrier',
    value: 60
  }, {
    name: 'Broker',
    value: 25
  }, {
    name: 'MGA',
    value: 15
  }];
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
  const getHeatmapColor = (value: number) => {
    if (value === 0) return '#F3F4F6'; // Grey for no data
    if (value < 30) return '#10B981';
    if (value < 40) return '#34D399';
    if (value < 50) return '#FBBF24';
    if (value < 60) return '#F59E0B';
    if (value < 70) return '#F97316';
    return '#EF4444';
  };
  if (loading) {
    return <div className="flex justify-center items-center h-96" data-id="element-2320">
        <div className="text-center" data-id="element-2321">
          <LoadingSpinner size="xl" data-id="element-2322" />
          <p className="mt-4 text-gray-600" data-id="element-2323">Loading dashboard data...</p>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2324">
    <div className="bg-white rounded-lg shadow p-6" data-id="element-2325">
      <h2 className="text-xl font-semibold mb-6" data-id="element-2326">Performance Overview</h2>
      <div className="grid md:grid-cols-3 gap-6" data-id="element-2327">
        <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2328">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-2329">Total Premium</h3>
          <p className="text-2xl font-bold" data-id="element-2330">
            £{stats.totalPremium.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500" data-id="element-2331">Based on uploaded bordereaux</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2332">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-2333">Total Claims</h3>
          <p className="text-2xl font-bold" data-id="element-2334">£{stats.totalClaims.toLocaleString()}</p>
          <p className="text-sm text-gray-500" data-id="element-2335">No claims data available</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg" data-id="element-2336">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-2337">Loss Ratio</h3>
          <p className="text-2xl font-bold" data-id="element-2338">{stats.lossRatio}%</p>
          <p className="text-sm text-gray-500" data-id="element-2339">Calculated from GWP & Claims</p>
        </div>
      </div>
    </div>

    {chartData.length > 0 ? <>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-2340">
          <h2 className="text-lg font-semibold mb-4" data-id="element-2341">
            Premium Trend
          </h2>
          <div className="h-96" data-id="element-2342">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2343">
              <ComposedChart data={chartData} data-id="element-2344">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2345" />
                <XAxis dataKey="date" tick={{
                fontSize: 12
              }} interval={0} angle={-45} textAnchor="end" data-id="element-2346" />
                <YAxis yAxisId="left" orientation="left" data-id="element-2347" />
                <Tooltip formatter={(value, name) => [`£${value.toLocaleString()}`, name === 'premium' ? 'Premium' : 'Claims']} data-id="element-2348" />
                <Legend data-id="element-2349" />
                <Bar yAxisId="left" dataKey="premium" fill="#3B82F6" name="Premium" barSize={20} data-id="element-2350" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-2351">
          <div className="bg-white rounded-lg shadow p-6" data-id="element-2352">
            <h2 className="text-lg font-semibold mb-4" data-id="element-2353">Loss Ratio Seasonality</h2>
            <div className="h-80" data-id="element-2354">
              {/* Simplified Heatmap for now since we have no loss ratio data */}
              <div className="flex items-center justify-center h-full text-gray-500" data-id="element-2355">
                No loss ratio data available to generate heatmap
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6" data-id="element-2356">
            <h2 className="text-lg font-semibold mb-4" data-id="element-2357">Revenue Split</h2>
            <div className="h-80" data-id="element-2358">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2359">
                <PieChart data-id="element-2360">
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value" label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-2361">
                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-2362" />)}
                  </Pie>
                  <Tooltip data-id="element-2363" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </> : <div className="bg-white rounded-lg shadow p-12 text-center" data-id="element-2364">
        <p className="text-gray-500 text-lg" data-id="element-2365">No data available. Please upload a bordereau to see analytics.</p>
      </div>}
  </div>;
}